import axios from "axios";
import store from "@/store";


import { GET_TOKEN } from "@/services/local.storage.service";
export default class ApiService {
  axiosInstance;

  constructor(apiUrl) {
    this.axiosInstance = axios.create();
    if (apiUrl) {
      this.axiosInstance.defaults.baseURL = apiUrl;
    }
    this.axiosInstance.defaults.withCredentials = true;
    this.axiosInstance.defaults.headers.common["Content-Type"] = "application/json";

    this.setupInterceptors()
  }

  get baseURL() {
    return this.axiosInstance.defaults.baseURL;
  }

  set baseURL(url) {
    this.axiosInstance.defaults.baseURL = url;
  }

    setupInterceptors() {
      this.axiosInstance.interceptors.request.use(
        config => this.request(config),
        error => this.errorRequest(error),
      );
  
      this.axiosInstance.interceptors.response.use(
        response => this.successHandler(response),
        error => this.errorHandler(error),
      );
    }

    request(config) {
       const noAuthUrl = config.url.includes("token") || config.url.includes(`refreshtoken`);
       if (noAuthUrl) return config;
       const token = GET_TOKEN();
       if (token) config.headers["Authorization"] = `Bearer ${token}`;
       return config;
      }

      errorRequest(error) {
        return Promise.reject(error);
      }
    
    
    errorHandler(error) {
  if (!error.response) {
    return Promise.reject("Network Error.");
  }

  // GENERAL ERROR
  if (error.response.status !== 401) {
    return Promise.reject(error.response.data);
  }

  // Login failed
  if (error.config.url.includes("auth/token")) {
    return Promise.reject(error.response.data);
  }

  // If this is the refresh token call and it failed → log out
  if (error.config.url.includes("auth/refreshtoken")) {
    store.dispatch("auth/START_LOGIN");
    return Promise.reject("Session expired. Please sign in.");
  }

  // If already retrying → avoid loop
  if (error.config.__isRetryRequest) {
    store.dispatch("auth/START_LOGIN");
    return Promise.reject("Session expired. Redirecting to login.");
  }

  // Refresh token logic
  return store.dispatch("auth/REFRESH_TOKEN")
    .then(() => {
      const newToken = GET_TOKEN();
      error.config.headers["Authorization"] = `Bearer ${newToken}`;
      error.config.__isRetryRequest = true; // prevent infinite loop
      return this.axiosInstance(error.config);
    })
    .catch((refreshError) => {
      store.dispatch("auth/START_LOGIN");
      return Promise.reject("Session expired. Please sign in.");
    });
}

  successHandler(response) {
    if (response.config.responseType === `blob`) {
      return Promise.resolve(response);
    } else if (response.data.status === "Success") {
      return Promise.resolve(response.data.result);
    } else {
      return Promise.reject(response.data.message);
    }
  }

  setHeader(token) {
    this.axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  removeHeader() {
    delete this.axiosInstance.defaults.headers.common["Authorization"];
  }

  get(resource, query) {
    this.updateBaseURL();
    if (query) resource += this.buildQuery(query);
    return this.axiosInstance.get(resource);
  }

  post(resource, params, query) {
    this.updateBaseURL();
    if (query) resource += this.buildQuery(query);
    return this.axiosInstance.post(resource, params);
  }

  put(resource, params, query) {
    this.updateBaseURL();
    if (query) resource += this.buildQuery(query);
    return this.axiosInstance.put(resource, params);
  }

  delete(resource, query) {
    this.updateBaseURL();
    if (query) resource += this.buildQuery(query);
    return this.axiosInstance.delete(resource);
  }

  patch(resource, params, query) {
    this.updateBaseURL();
    if (query) resource += this.buildQuery(query);
    return this.axiosInstance.patch(resource, params);
  }

  download(resource) {
    this.updateBaseURL();
    return this.axiosInstance.get(resource, { responseType: "blob" });
  }

  downloadPost(resource, payload) {
    this.updateBaseURL();
    return this.axiosInstance.post(resource, payload, { responseType: "blob" });
  }

  upload(resource, formData) {
    this.updateBaseURL();
    var config = { headers: { "Content-Type": "multipart/form-data" } };
    return this.axiosInstance.post(resource, formData, config);
  }

  updateBaseURL() {
    // Override in subclasses if baseURL is dynamic
    if (this.baseURL && this.axiosInstance.defaults.baseURL !== this.baseURL) {
      this.axiosInstance.defaults.baseURL = this.baseURL;
    }
  }

  buildQuery(qObject) {
    let retVal = "";
    let keys = Object.keys(qObject);
    keys.forEach((x, i) => {
      if (i === 0) retVal += `?${x}=${qObject[x]}`;
      else retVal += `&${x}=${qObject[x]}`;
    });
    return retVal;
  }
}
